/**
 * The one-way delete, as a panel.
 *
 * Used by the program and the activity screens. One component rather than two
 * near-identical blocks, because the frictions below are the entire safety of
 * the operation, and a copy is a copy that will eventually be missing one of
 * them.
 *
 * Four frictions, and each is answering a different way this goes wrong:
 *
 *   - **It is last on the page and behind a disclosure.** A destructive control
 *     sitting open beside "Edit" gets clicked by someone reaching for "Edit".
 *   - **It says what will happen, in specifics.** Not "this cannot be undone" on
 *     its own — how many activities go with it, and that the hours stop being
 *     counted. Those are the two consequences an admin does not predict.
 *   - **It requires a reason, of at least five characters.** The API requires it
 *     too. Six months later, "why did last quarter's hours drop" has to have an
 *     answer that does not depend on anyone remembering.
 *   - **It reports the deleted state plainly.** Once done, the panel stops
 *     offering anything and says so, because a disabled button invites a second
 *     attempt.
 *
 * What it deliberately does *not* do is ask the admin to type the record's name.
 * That friction belongs on an operation with no undo *and* no trace — this one
 * leaves the row, the audit entry and the reason behind it.
 */

import { useState } from 'react';
import type { ReactNode } from 'react';
import { Button } from './Button';
import { Card } from './Card';
import { TextArea } from '../forms/Field';
import styles from './DeletePanel.module.css';

export interface DeletePanelProps {
  /** Card heading, e.g. "Delete this program". */
  title: string;
  /** The word for what is being deleted, lower case: "program", "activity". */
  noun: string;
  /** Already gone. The panel then explains rather than offers. */
  deleted: boolean;
  /** What will happen, spelled out. Rendered above the reason field. */
  consequences: ReactNode;
  pending: boolean;
  /** Whatever the API said, if it refused. */
  error?: Error | null;
  onDelete: (reason: string) => void;
}

export function DeletePanel({
  title,
  noun,
  deleted,
  consequences,
  pending,
  error,
  onDelete,
}: DeletePanelProps) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [problem, setProblem] = useState<string | undefined>();

  if (deleted) {
    return (
      <Card title={title}>
        <p className={styles.gone}>
          This {noun} has been deleted. It no longer counts towards any figure on the dashboard, it
          cannot be edited, and it cannot be brought back. The record and its history remain, so
          what happened is still readable.
        </p>
      </Card>
    );
  }

  const confirm = () => {
    if (reason.trim().length < 5) {
      setProblem('Please give a reason of at least 5 characters.');
      return;
    }
    onDelete(reason.trim());
  };

  return (
    <Card title={title}>
      {!open ? (
        <div className={styles.closed}>
          <p className={styles.explain}>
            Deleting a {noun} is permanent. If it simply is not running at the moment, change its
            status instead — that reverses, and this does not.
          </p>
          <Button variant="danger" onClick={() => setOpen(true)}>
            Delete this {noun}
          </Button>
        </div>
      ) : (
        <div className={styles.confirm}>
          <div className={styles.warning}>{consequences}</div>

          <TextArea
            label={`Why is this ${noun} being deleted?`}
            required
            rows={2}
            value={reason}
            error={problem}
            onChange={(event) => {
              setReason(event.target.value);
              setProblem(undefined);
            }}
          />

          {error && (
            <p className={styles.error} role="alert">
              {error.message}
            </p>
          )}

          <div className={styles.actions}>
            <Button variant="danger" loading={pending} onClick={confirm}>
              Yes, delete this {noun}
            </Button>
            <Button
              onClick={() => {
                setOpen(false);
                setReason('');
                setProblem(undefined);
              }}
              disabled={pending}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
